/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from 'react';
import './AdminUsers.css';
import './CourseQuestions.css';
import { apiFetch } from './api';
import MathText from './MathText';
import CourseDropdown from './CourseDropdown';
import QuestionMappingModal from './QuestionMappingModal';
import { useActiveCourse } from './ActiveCourseContext';

const QUESTION_CATEGORIES = [
  { key: 'pretest', label: 'Öntest', description: 'Ders başlamadan önce öğrencinin hazırbulunuşluğunu ölçen sorular.' },
  { key: 'quiz', label: 'Kısa Sınav', description: 'Kısa süreli değerlendirmelerde kullanılacak sorular.' },
  { key: 'exam', label: 'Sınav', description: 'Vize, final ve diğer kapsamlı sınavlarda kullanılacak sorular.' },
  { key: 'legacy', label: 'Geçmiş Yıl Soruları', description: 'Kategori sistemi kullanılmadan önceki akademik yıllarda kaydedilen sorular.' },
];

export function getQuestionMissingFields(q) {
  // Çalışma soruları sözlü pratik içindir; sınav ÖÇ/PÇ eşleştirmesine girmez.
  if (q.category === 'study') return [];
  return [
    q.week_number == null ? 'Hafta' : null,
    !(Array.isArray(q.learning_outcomes) && q.learning_outcomes.length > 0) ? 'ÖÇ' : null,
    !(Array.isArray(q.program_outcomes) && q.program_outcomes.length > 0) ? 'PÇ' : null,
  ].filter(Boolean);
}

// Hafta, ÖÇ veya PÇ bilgisinden biri eksikse soru sınavda kullanılamaz.
// Eksik nedeni kullanıcıya ayrı gösterilir; ÖÇ/PÇ doluyken "eşlenmemiş" denmez.
export function isQuestionUnmapped(q) {
  return getQuestionMissingFields(q).length > 0;
}

// Yanıtlar eski kayıtlarda şık harfi ("D") veya şık metni ("Hibrit bulut")
// olarak tutulabiliyor. Türkçe I/İ farkı nedeniyle görünür eşleştirmeyi
// bozmayacak ortak karşılaştırma biçimi.
const normalizeAnswerText = (value) => String(value ?? '')
  .normalize('NFKC')
  .replace(/\s+/g, ' ')
  .trim()
  .toLocaleUpperCase('tr-TR');

export const formatQuestionCreatedAt = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

function QuestionCard({ question, index, onSetCorrect, onDelete, onMap, onCategoryChange, isPretest = false, readOnly = false }) {
  const unmapped = !isPretest && isQuestionUnmapped(question);
  const missingFields = !isPretest ? getQuestionMissingFields(question) : [];
  const missingLabel = `${missingFields.join(' / ')} eksik`;
  const isMC = question.question_type === 'MultipleChoice';
  const options = ['A', 'B', 'C', 'D', 'E'];
  const createdAtLabel = formatQuestionCreatedAt(question.created_at);

  const getCorrectLetter = () => {
    if (!question.correct_answer) return null;
    const ca = normalizeAnswerText(question.correct_answer);
    if (ca.length === 1 && 'ABCDE'.includes(ca)) return ca;
    const matchingOption = options.find((letter) => {
      const value = question[`option_${letter.toLowerCase()}`];
      return value && normalizeAnswerText(value) === ca;
    });
    if (matchingOption) return matchingOption;
    return null;
  };

  const correctLetter = getCorrectLetter();
  const [selected, setSelected] = useState(correctLetter);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // doğru cevap dışarıdan güncellenince senkronize et
  React.useEffect(() => {
    setSelected(getCorrectLetter());
    setSaved(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.correct_answer]);

  const handleSave = async () => {
    if (!selected || saving) return;
    setSaving(true);
    setSaved(false);
    await onSetCorrect(question.id, selected);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const isDirty = selected !== correctLetter;

  return (
    <div className={`cq-question-card${unmapped ? ' cq-question-card--unmapped' : ''}`}>
      <div className="cq-question-header">
        <span className="cq-q-number">{index + 1}.</span>
        <span className={`cq-type-badge ${isMC ? 'cq-type-mc' : 'cq-type-classic'}`}>
          {isPretest ? 'Öntest' : (isMC ? 'Çoktan Seçmeli' : 'Klasik')}
        </span>
        {createdAtLabel && (
          <span className="cq-created-at" title={`Oluşturulma tarihi: ${createdAtLabel}`}>
            Oluşturulma: {createdAtLabel}
          </span>
        )}
        {question.week_number != null && (
          <span className="cq-week-badge">{question.week_number}. Hafta</span>
        )}
        {unmapped && (
          <span
            className="cq-unmapped-badge"
            title={`${missingLabel}. Bu soru sınavlarda sorulamaz.`}
          >
            ⚠ {missingLabel}
          </span>
        )}
        {unmapped && onMap && (
          <button
            className="cq-map-btn"
            title={`${missingFields.join(' ve ')} bilgisini tamamla`}
            onClick={() => onMap(question)}
          >
            Tamamla
          </button>
        )}
        {!isPretest && onCategoryChange && (
          <select
            className="cq-question-category-select"
            value={question.category || 'uncategorized'}
            aria-label={`${index + 1}. sorunun kategorisi`}
            onChange={(event) => onCategoryChange(question.id, event.target.value)}
          >
            <option value="uncategorized" disabled>Kategorisiz</option>
            <option value="quiz">Kısa Sınav</option>
            <option value="exam">Sınav</option>
          </select>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', flexWrap: 'wrap', gap: '0.3rem', alignItems: 'center' }}>
        {question.learning_outcomes && question.learning_outcomes.length > 0 && (
          <div className="cq-outcome-badges">
            {[...question.learning_outcomes]
              .sort((a, b) => (parseInt(a.label.replace('ÖÇ', ''), 10) || 0) - (parseInt(b.label.replace('ÖÇ', ''), 10) || 0))
              .map((lo) => (
                <span key={lo.id} className="cq-outcome-badge cq-lo-badge" title={lo.name}>{lo.label}</span>
              ))}
          </div>
        )}
        {question.program_outcomes && question.program_outcomes.length > 0 && (
          <div className="cq-outcome-badges">
            {[...question.program_outcomes]
              .sort((a, b) => (parseInt(a.label.replace('PÇ', ''), 10) || 0) - (parseInt(b.label.replace('PÇ', ''), 10) || 0))
              .map((po) => (
                <span key={po.id} className="cq-outcome-badge cq-po-badge" title={po.name}>{po.label}</span>
              ))}
          </div>
        )}
        {onDelete && (
          <button
            className="cq-delete-btn"
            title="Soruyu sil"
            onClick={() => {
              if (window.confirm('Bu soruyu silmek istediğinize emin misiniz?')) onDelete(question.id);
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
            </svg>
            Sil
          </button>
        )}
        </div>
      </div>
      <div className="cq-question-text"><MathText text={question.question_text} /></div>
      {question.image_url && (() => {
        const MEDIA_BASE = process.env.REACT_APP_QUESTIONS_MEDIA_URL || 'http://localhost:5105';
        const src = question.image_url.startsWith('http') ? question.image_url : `${MEDIA_BASE}${question.image_url}`;
        return (
          <div style={{ margin: '8px 0 12px', textAlign: 'center' }}>
            <img
              src={src}
              alt="Soru görseli"
              style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'zoom-in' }}
              onClick={() => window.open(src, '_blank')}
            />
          </div>
        );
      })()}

      {isMC && (
        <>
          <div className="cq-options-list">
            {options.map((letter) => {
              const text = question[`option_${letter.toLowerCase()}`];
              if (!text) return null;
              const isSelected = selected === letter;
              return (
                <label
                  key={letter}
                  className={`cq-option-row${isSelected ? ' correct' : ''}`}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <input
                    type="radio"
                    name={`correct-answer-${question.id}`}
                    value={letter}
                    checked={isSelected}
                    onChange={() => !readOnly && setSelected(letter)}
                    disabled={readOnly}
                    style={{ accentColor: '#16a34a', width: 16, height: 16, flexShrink: 0 }}
                  />
                  <span className="cq-option-letter">{letter})</span>
                  <span><MathText text={text} /></span>
                </label>
              );
            })}
          </div>
          {onSetCorrect && <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.75rem' }}>
            <button
              className="assign-btn assign-btn-primary"
              style={{ padding: '0.35rem 1rem', fontSize: '0.83rem' }}
              disabled={!selected || !isDirty || saving}
              onClick={handleSave}
            >
              {saving ? 'Kaydediliyor...' : 'Doğru Seçeneği İşaretle'}
            </button>
            {saved && (
              <span style={{ color: '#16a34a', fontSize: '0.82rem', fontWeight: 500 }}>✓ Kaydedildi</span>
            )}
            {!correctLetter && !selected && (
              <span style={{ color: '#f59e0b', fontSize: '0.82rem' }}>Doğru şık işaretlenmemiş</span>
            )}
          </div>}
        </>
      )}

      {!isMC && question.correct_answer && (
        <div className="cq-classic-answer">
          <div className="cq-classic-answer-label">Cevap</div>
          <div className="cq-classic-answer-text"><MathText text={question.correct_answer} /></div>
        </div>
      )}
    </div>
  );
}

function CourseQuestions({ userType }) {
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');

  const [questions, setQuestions] = useState([]);
  const [pretestQuestions, setPretestQuestions] = useState([]);
  const [activeCategory, setActiveCategory] = useState('exam');
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsLoaded, setQuestionsLoaded] = useState(false);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterWeek, setFilterWeek] = useState('all');
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);
  const [mappingQuestion, setMappingQuestion] = useState(null);
  const activeCourseContext = useActiveCourse();

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoadingCourses(true);
        setError('');
        const response = await apiFetch('/instructor/courses');
        const data = await response.json();
        if (data.success) {
          setCourses(data.courses || []);
        } else {
          setError(data.message || 'Dersler yüklenemedi');
        }
      } catch {
        setError('Bağlantı hatası');
      } finally {
        setLoadingCourses(false);
      }
    };
    fetchCourses();
  }, []);

  const programs = useMemo(() => {
    const seen = new Set();
    const list = [];
    courses.forEach((c) => {
      const p = c.program_name || '';
      if (p && !seen.has(p)) {
        seen.add(p);
        list.push(p);
      }
    });
    return list.sort((a, b) => a.localeCompare(b, 'tr'));
  }, [courses]);

  const filteredCourses = useMemo(() => {
    if (!selectedProgram) return [];
    return courses.filter((c) => (c.program_name || '') === selectedProgram);
  }, [courses, selectedProgram]);

  const handleDeleteQuestion = async (questionId, source = 'question') => {
    try {
      const res = await apiFetch(
        `/instructor/courses/${selectedCourseId}/questions/${questionId}?source=${encodeURIComponent(source)}`,
        { method: 'DELETE' }
      );
      const data = await res.json();
      if (data.success) {
        if (source === 'pretest') {
          setPretestQuestions((prev) => prev.filter((q) => q.id !== questionId));
        } else {
          setQuestions((prev) => prev.filter((q) => q.id !== questionId));
        }
      } else {
        alert(`⚠️ ${data.message || 'Soru silinemedi'}`);
      }
    } catch {
      setError('Bağlantı hatası');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Bu kategorideki TÜM ${categoryQuestions.length} soruyu silmek istediğinize emin misiniz?`)) return;
    if (!window.confirm('Bu işlem GERİ ALINAMAZ! Devam edilsin mi?')) return;
    try {
      const res = await apiFetch(`/instructor/courses/${selectedCourseId}/questions/delete-all?category=${encodeURIComponent(activeCategory)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        await fetchQuestions(selectedCourseId);
        setFilterType('all');
        setFilterWeek('all');
        if (data.protected_count > 0) {
          alert(`${data.deleted_count} soru silindi.\n\n⚠️ ${data.protected_count} soru silinemedi çünkü bu sorular daha önce yapılmış sınavlarda kullanılmıştır. Sınav geçmişinin korunması için bu sorular havuzda tutulmaktadır.`);
        }
      } else {
        setError(data.message || 'Sorular silinemedi');
      }
    } catch {
      setError('Bağlantı hatası');
    }
  };

  const handleDeleteByType = async (type) => {
    const label = type === 'MultipleChoice' ? 'Çoktan Seçmeli' : 'Klasik';
    const count = categoryQuestions.filter((q) => q.question_type === type).length;
    if (!window.confirm(`${count} adet ${label} soruyu silmek istediğinize emin misiniz?`)) return;
    try {
      const res = await apiFetch(`/instructor/courses/${selectedCourseId}/questions/delete-by-type/${type}?category=${encodeURIComponent(activeCategory)}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        await fetchQuestions(selectedCourseId);
        if (filterType === type) setFilterType('all');
        if (data.protected_count > 0) {
          alert(`${data.deleted_count} ${label.toLowerCase()} soru silindi.\n\n⚠️ ${data.protected_count} soru silinemedi çünkü bu sorular daha önce yapılmış sınavlarda kullanılmıştır. Sınav geçmişinin korunması için bu sorular havuzda tutulmaktadır.`);
        }
      } else {
        setError(data.message || 'Sorular silinemedi');
      }
    } catch {
      setError('Bağlantı hatası');
    }
  };

  const handleDeleteByWeek = async () => {
    if (filterWeek === 'all' || filterWeek === 'none') return;
    const weekQuestions = categoryQuestions.filter((q) => Number(q.week_number) === Number(filterWeek));
    if (!weekQuestions.length) return;
    if (!window.confirm(`${filterWeek}. haftadaki ${weekQuestions.length} soruyu silmek istediğinize emin misiniz?`)) return;
    if (!window.confirm('Bu işlem geri alınamaz. Daha önce öğrencinin çözdüğü sınavlarda kullanılan sorular korunacaktır. Devam edilsin mi?')) return;
    try {
      const res = await apiFetch(`/instructor/courses/${selectedCourseId}/questions/delete-by-week/${filterWeek}?category=${encodeURIComponent(activeCategory)}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Haftanın soruları silinemedi');
        return;
      }
      await fetchQuestions(selectedCourseId);
      setFilterWeek('all');
      alert(data.protected_count > 0
        ? `${data.deleted_count} soru silindi. ${data.protected_count} soru daha önce kullanıldığı için korundu.`
        : `${data.deleted_count} soru silindi.`);
    } catch {
      setError('Bağlantı hatası');
    }
  };

  const handleSetCorrect = async (questionId, letter, isPretest = false) => {
    try {
      const res = await apiFetch(`/instructor/courses/${selectedCourseId}/questions/${questionId}/correct-answer`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correct_answer: letter, source: isPretest ? 'pretest' : 'question' }),
      });
      const data = await res.json();
      if (data.success) {
        const updateAnswer = (items) => items.map((q) => (
          q.id === questionId ? { ...q, correct_answer: data.correct_answer } : q
        ));
        if (isPretest) setPretestQuestions(updateAnswer);
        else setQuestions(updateAnswer);
      }
    } catch {
      // sessiz hata
    }
  };

  const handleCategoryChange = async (questionId, category) => {
    try {
      const res = await apiFetch(`/instructor/courses/${selectedCourseId}/questions/${questionId}/category`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Kategori güncellenemedi');
        return;
      }
      setQuestions((prev) => prev.map((q) => (
        q.id === questionId ? { ...q, category: data.category } : q
      )));
    } catch {
      setError('Kategori güncellenirken bağlantı hatası oluştu');
    }
  };

  const fetchQuestions = async (courseId) => {
    try {
      setLoadingQuestions(true);
      setError('');
      setQuestions([]);
      setPretestQuestions([]);
      setQuestionsLoaded(false);
      const response = await apiFetch(`/instructor/courses/${courseId}/questions`);
      const data = await response.json();
      if (data.success) {
        // Çalışma konuları ve ödevler ayrı ekranlarda tutulur; bu kayıtlar
        // öğretmenin Sorular alanına geri düşmemelidir.
        const regularQuestions = (data.questions || []).filter(
          (q) => q.category !== 'study' && q.category !== 'homework'
        );
        const transferredPretests = data.pretest_questions || [];
        setQuestions(regularQuestions);
        setPretestQuestions(transferredPretests);
        const firstPopulatedCategory = QUESTION_CATEGORIES.find(({ key }) => (
          key === 'pretest'
            ? transferredPretests.length > 0
            : regularQuestions.some((q) => (q.category || 'uncategorized') === key)
        ));
        setActiveCategory(firstPopulatedCategory?.key || 'uncategorized');
        setQuestionsLoaded(true);
      } else {
        setError(data.message || 'Sorular yüklenemedi');
      }
    } catch {
      setError('Bağlantı hatası');
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleProgramChange = (program) => {
    setSelectedProgram(program);
    setSelectedCourseId('');
    setQuestions([]);
    setPretestQuestions([]);
    setActiveCategory('exam');
    setQuestionsLoaded(false);
    setError('');
  };

  const handleCourseChange = (courseId) => {
    setSelectedCourseId(courseId);
    setQuestions([]);
    setPretestQuestions([]);
    setActiveCategory('exam');
    setQuestionsLoaded(false);
    setError('');
    setFilterType('all');
    setFilterWeek('all');
    setShowUnmappedOnly(false);
    if (courseId) fetchQuestions(courseId);
    if (activeCourseContext) activeCourseContext.setActiveCourseId(courseId);
  };

  useEffect(() => {
    if (!activeCourseContext) return;
    const nextCourseId = activeCourseContext.activeCourseId;
    if (String(selectedCourseId) === String(nextCourseId)) return;
    setSelectedCourseId(nextCourseId);
    setQuestions([]);
    setPretestQuestions([]);
    setActiveCategory('exam');
    setQuestionsLoaded(false);
    setError('');
    setFilterType('all');
    setFilterWeek('all');
    setShowUnmappedOnly(false);
    if (nextCourseId) fetchQuestions(nextCourseId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCourseContext?.activeCourseId, userType]);

  const handleFilterClick = (type) => {
    setFilterType((prev) => (prev === type ? 'all' : type));
  };

  const selectedCourse = courses.find((c) => String(c.course_id) === String(selectedCourseId));
  const categoryQuestions = useMemo(
    () => questions.filter((q) => (q.category || 'legacy') === activeCategory),
    [questions, activeCategory]
  );
  const categoryCounts = useMemo(() => {
    const counts = { pretest: pretestQuestions.length, quiz: 0, exam: 0, legacy: 0 };
    questions.forEach((q) => {
      const key = q.category || 'legacy';
      if (Object.prototype.hasOwnProperty.call(counts, key)) counts[key] += 1;
      else counts.legacy += 1;
    });
    return counts;
  }, [questions, pretestQuestions]);
  const mcCount = categoryQuestions.filter((q) => q.question_type === 'MultipleChoice').length;
  const classicCount = categoryQuestions.length - mcCount;
  const selectedCategory = QUESTION_CATEGORIES.find((category) => category.key === activeCategory);

  const availableWeeks = useMemo(() => {
    const set = new Set();
    let hasNull = false;
    categoryQuestions.forEach((q) => {
      if (q.week_number == null) hasNull = true;
      else set.add(Number(q.week_number));
    });
    const list = Array.from(set).sort((a, b) => a - b);
    return { list, hasNull };
  }, [categoryQuestions]);

  const unmappedQuestions = useMemo(
    () => categoryQuestions.filter((q) => isQuestionUnmapped(q)),
    [categoryQuestions]
  );
  const unmappedCount = unmappedQuestions.length;
  const missingFieldCounts = useMemo(() => unmappedQuestions.reduce((counts, question) => {
    getQuestionMissingFields(question).forEach((field) => { counts[field] += 1; });
    return counts;
  }, { Hafta: 0, 'ÖÇ': 0, 'PÇ': 0 }), [unmappedQuestions]);

  // Modal'dan kaydedilen eşleştirmeyi yerel listeye işle (anında yenilensin)
  const handleMappingSaved = (questionId, mapping) => {
    setQuestions((prev) => prev.map((q) =>
      String(q.id) === String(questionId)
        ? {
            ...q,
            week_number: mapping.week_number,
            learning_outcomes: mapping.learning_outcomes || [],
            program_outcomes: mapping.program_outcomes || [],
          }
        : q
    ));
  };

  const displayedQuestions = useMemo(() => {
    return categoryQuestions.filter((q) => {
      if (showUnmappedOnly && !isQuestionUnmapped(q)) return false;
      if (filterType !== 'all' && q.question_type !== filterType) return false;
      if (filterWeek === 'all') return true;
      if (filterWeek === 'none') return q.week_number == null;
      return Number(q.week_number) === Number(filterWeek);
    });
  }, [categoryQuestions, filterType, filterWeek, showUnmappedOnly]);

  return (
    <div className="au-page">
      <div className="au-toolbar">
        <h2 className="au-title">Sorular</h2>
      </div>

      {/* Aktif ders çubuğu olmayan ekranlarda ders seçimi burada yapılır. */}
      {!activeCourseContext && (
        <div className="cq-selector-panel">
          <CourseDropdown
            courses={courses}
            selectedCourseId={selectedCourseId}
            onChange={handleCourseChange}
            loading={loadingCourses}
          />
        </div>
      )}

      {/* Hata mesajı */}
      {error && (
        <div className="cq-error">
          <span>{error}</span>
        </div>
      )}

      {/* Ders seçilmedi */}
      {!error && !selectedCourseId && (
        <div className="cq-empty">
          <div className="cq-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
            </svg>
          </div>
          <p className="cq-empty-text">
            {activeCourseContext
              ? 'Soruları görüntülemek için üstteki Aktif Ders alanından bir ders seçin.'
              : 'Soruları görüntülemek için bir ders seçin.'}
          </p>
        </div>
      )}

      {/* Yükleniyor */}
      {!error && selectedCourseId && loadingQuestions && (
        <div className="cq-loading">
          <div className="cq-spinner" />
          <p>Sorular yükleniyor...</p>
        </div>
      )}

      {/* Soru listesi */}
      {!error && selectedCourseId && !loadingQuestions && questionsLoaded && (
        <>
          {selectedCourse && (
            <div className="cq-course-title">
              <span className="cq-course-title-badge">
                {selectedCourse.course_code || selectedCourse.course_name}
              </span>
              {selectedCourse.course_code && (
                <span className="cq-course-title-name">{selectedCourse.course_name}</span>
              )}
            </div>
          )}

          <div className="cq-category-tabs" role="tablist" aria-label="Soru kategorileri">
            {QUESTION_CATEGORIES.filter((category) => category.key !== 'legacy' || categoryCounts.legacy > 0).map((category) => (
              <button
                key={category.key}
                type="button"
                role="tab"
                aria-selected={activeCategory === category.key}
                title={category.description}
                className={`cq-category-tab${category.key === 'pretest' ? ' cq-category-tab--pretest' : ''}${activeCategory === category.key ? ' active' : ''}`}
                onClick={() => {
                  setActiveCategory(category.key);
                  setFilterType('all');
                  setFilterWeek('all');
                  setShowUnmappedOnly(false);
                }}
              >
                <span>{category.label}</span>
                <span className="cq-category-count">{categoryCounts[category.key] || 0}</span>
              </button>
            ))}
          </div>
          {selectedCategory && (
            <p className="cq-category-description">{selectedCategory.description}</p>
          )}

          {activeCategory !== 'pretest' && (categoryQuestions.length === 0 ? (
            <div className="cq-empty">
              <div className="cq-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <p className="cq-empty-text">Bu kategoride henüz soru bulunmuyor.</p>
            </div>
          ) : (
            <>
              {unmappedCount > 0 && (
                <div
                  className={`cq-unmapped-banner${showUnmappedOnly ? ' active' : ''}`}
                  onClick={() => setShowUnmappedOnly((v) => !v)}
                  title="Eksik bilgili soruları göster/gizle"
                >
                  <span className="cq-unmapped-banner-icon">⚠</span>
                  <span className="cq-unmapped-banner-text">
                    <strong>{unmappedCount}</strong> soruda eksik bilgi var
                    {' '}({[
                      missingFieldCounts.Hafta > 0 ? `${missingFieldCounts.Hafta} hafta` : null,
                      missingFieldCounts['ÖÇ'] > 0 ? `${missingFieldCounts['ÖÇ']} ÖÇ` : null,
                      missingFieldCounts['PÇ'] > 0 ? `${missingFieldCounts['PÇ']} PÇ` : null,
                    ].filter(Boolean).join(', ')} eksik).
                    Bu sorular sınavlarda sorulamaz.
                  </span>
                  <span className="cq-unmapped-banner-action">
                    {showUnmappedOnly ? 'Tümünü göster' : 'Eksikleri göster'}
                  </span>
                </div>
              )}
              <div className="cq-summary-bar">
                <span
                  className={`cq-count-badge total${filterType === 'all' ? ' cq-count-badge--active' : ''}`}
                  onClick={() => setFilterType('all')}
                  title="Tümünü göster"
                >
                  Toplam: {categoryQuestions.length}
                </span>
                {mcCount > 0 && (
                  <span
                    className={`cq-count-badge mc${filterType === 'MultipleChoice' ? ' cq-count-badge--active' : ''}`}
                    onClick={() => handleFilterClick('MultipleChoice')}
                    title="Sadece çoktan seçmeli"
                  >
                    Çoktan Seçmeli: {mcCount}
                  </span>
                )}
                {classicCount > 0 && (
                  <span
                    className={`cq-count-badge classic${filterType === 'Classic' ? ' cq-count-badge--active' : ''}`}
                    onClick={() => handleFilterClick('Classic')}
                    title="Sadece klasik"
                  >
                    Klasik: {classicCount}
                  </span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {activeCategory !== 'legacy' && filterType === 'all' && categoryQuestions.length > 0 && (
                    <button className="cq-bulk-delete-btn" onClick={handleDeleteAll}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                      </svg>
                      Tümünü Sil
                    </button>
                  )}
                  {activeCategory !== 'legacy' && filterType === 'all' && filterWeek !== 'all' && filterWeek !== 'none' && (
                    <button className="cq-bulk-delete-btn" onClick={handleDeleteByWeek}>
                      {filterWeek}. Haftayı Sil
                    </button>
                  )}
                  {activeCategory !== 'legacy' && filterType === 'MultipleChoice' && mcCount > 0 && (
                    <button className="cq-bulk-delete-btn" onClick={() => handleDeleteByType('MultipleChoice')}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                      </svg>
                      Çoktan Seçmeli Soruları Sil
                    </button>
                  )}
                  {activeCategory !== 'legacy' && filterType === 'Classic' && classicCount > 0 && (
                    <button className="cq-bulk-delete-btn" onClick={() => handleDeleteByType('Classic')}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                      </svg>
                      Klasik Soruları Sil
                    </button>
                  )}
                </div>
              </div>
              {(availableWeeks.list.length > 0 || availableWeeks.hasNull) && (
                <div className="cq-week-filter">
                  <span className="cq-week-filter-label">Hafta:</span>
                  <button
                    type="button"
                    className={`cq-week-chip${filterWeek === 'all' ? ' active' : ''}`}
                    onClick={() => setFilterWeek('all')}
                  >
                    Tümü
                  </button>
                  {availableWeeks.list.map((w) => (
                    <button
                      key={w}
                      type="button"
                      className={`cq-week-chip${String(filterWeek) === String(w) ? ' active' : ''}`}
                      onClick={() => setFilterWeek(w)}
                    >
                      {w}. Hafta
                    </button>
                  ))}
                  {availableWeeks.hasNull && (
                    <button
                      type="button"
                      className={`cq-week-chip${filterWeek === 'none' ? ' active' : ''}`}
                      onClick={() => setFilterWeek('none')}
                    >
                      Belirtilmemiş
                    </button>
                  )}
                </div>
              )}
              {displayedQuestions.length === 0 ? (
                <div className="cq-empty">
                  <p className="cq-empty-text">
                    {showUnmappedOnly ? 'Eksik bilgili soru bulunamadı.' : 'Bu türde soru bulunamadı.'}
                  </p>
                </div>
              ) : (
                <div className="cq-question-list">
                  {displayedQuestions.map((q, i) => (
                    <QuestionCard
                      key={q.id}
                      question={q}
                      index={i}
                      onSetCorrect={activeCategory === 'legacy' ? null : handleSetCorrect}
                      onDelete={activeCategory === 'legacy' ? null : handleDeleteQuestion}
                      onMap={activeCategory === 'legacy' ? null : (question) => setMappingQuestion(question)}
                      onCategoryChange={activeCategory === 'legacy' ? null : handleCategoryChange}
                      readOnly={activeCategory === 'legacy'}
                    />
                  ))}
                </div>
              )}
            </>
          ))}

          {activeCategory === 'pretest' && (pretestQuestions.length > 0 ? (
            <section className="cq-pretest-section" aria-labelledby="cq-pretest-title">
              <div className="cq-pretest-heading">
                <div>
                  <h3 id="cq-pretest-title">Öntest Soruları</h3>
                  <p>Öğrenme çıktısı analizinden bu ders için aktarılan sorular</p>
                </div>
                <span className="cq-pretest-count">{pretestQuestions.length} soru</span>
              </div>
              <div className="cq-question-list">
                {pretestQuestions.map((q, i) => (
                  <QuestionCard
                    key={`pretest-${q.id}`}
                    question={q}
                    index={i}
                    isPretest
                    onSetCorrect={(questionId, letter) => handleSetCorrect(questionId, letter, true)}
                    onDelete={(questionId) => handleDeleteQuestion(questionId, 'pretest')}
                  />
                ))}
              </div>
            </section>
          ) : (
            <div className="cq-empty">
              <div className="cq-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <p className="cq-empty-text">Bu ders için aktarılmış öntest sorusu bulunmuyor.</p>
            </div>
          ))}
        </>
      )}

      {mappingQuestion && (
        <QuestionMappingModal
          courseId={selectedCourseId}
          questions={[mappingQuestion]}
          onClose={() => setMappingQuestion(null)}
          onSaved={handleMappingSaved}
        />
      )}
    </div>
  );
}

export default CourseQuestions;
